import { createContext, useCallback, useContext, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/* ------------------------------- Marca ------------------------------- */

export const Star = ({ size = 16, color = 'var(--terracota)', className = '' }: { size?: number; color?: string; className?: string }) => (
  <svg className={className} width={size * 0.8} height={size} viewBox="0 0 24 30" aria-hidden="true">
    <path d="M12 0C12.9 9 15.5 13.6 24 15C15.5 16.4 12.9 21 12 30C11.1 21 8.5 16.4 0 15C8.5 13.6 11.1 9 12 0Z" fill={color} />
  </svg>
)

export const Logo = ({ size = 30, tagline = true }: { size?: number; tagline?: boolean }) => (
  <div className="logo" style={{ fontSize: size }}>
    <div className="logo-word">REVEL<span className="logo-a">A<Star size={size * 0.62} className="logo-star" /></span></div>
    {tagline && <div className="logo-tag">HAY MÁS DE LO QUE VES</div>}
  </div>
)

/* ------------------------------- Íconos ------------------------------- */

const paths: Record<string, string> = {
  sparkle: 'M12 3c.6 4.2 2.8 6.4 7 7-4.2.6-6.4 2.8-7 7-.6-4.2-2.8-6.4-7-7 4.2-.6 6.4-2.8 7-7ZM19 15.5c.25 1.6 1 2.3 2.5 2.5-1.5.25-2.25 1-2.5 2.5-.25-1.5-1-2.25-2.5-2.5 1.5-.2 2.25-.9 2.5-2.5Z',
  home: 'M3 10.5 12 3l9 7.5M5 9v11h5v-6h4v6h5V9',
  building: 'M4 21V5l8-3 8 3v16M9 21v-4h6v4M8 8h.01M12 8h.01M16 8h.01M8 12h.01M12 12h.01M16 12h.01',
  users: 'M16 20v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M21 20v-1a4 4 0 0 0-3-3.9M15.5 4.1a3.5 3.5 0 0 1 0 6.8',
  calc: 'M6 2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2ZM8 6h8v3H8zM8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5',
  megaphone: 'M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1ZM15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13',
  calendar: 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM16 2v4M8 2v4M3 10h18',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5zM4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5',
  check: 'M20 6 9 17l-5-5',
  checksq: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  plus: 'M12 5v14M5 12h14',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  x: 'M18 6 6 18M6 6l12 12',
  left: 'M15 18l-6-6 6-6',
  right: 'M9 18l6-6-6-6',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  chat: 'M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.6A8.5 8.5 0 1 1 21 11.5Z',
  map: 'M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11ZM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  refresh: 'M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  kanban: 'M4 3h4v18H4zM10 3h4v12h-4zM16 3h4v8h-4z',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z',
  external: 'M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
  image: 'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2ZM8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M21 15l-5-5L5 21',
  printer: 'M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  menu: 'M3 6h18M3 12h18M3 18h18',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  layers: 'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  bed: 'M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9',
  ruler: 'M21.3 8.7 8.7 21.3a1 1 0 0 1-1.4 0l-4.6-4.6a1 1 0 0 1 0-1.4L15.3 2.7a1 1 0 0 1 1.4 0l4.6 4.6a1 1 0 0 1 0 1.4ZM7.5 10.5l2 2M10.5 7.5l2 2M13.5 4.5l2 2M4.5 13.5l2 2',
}

export const Icon = ({ name, size = 18, className = '' }: { name: keyof typeof paths | string; size?: number; className?: string }) => (
  <svg className={`icon ${className}`} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={paths[name] ?? ''} />
  </svg>
)

/* ------------------------------ Botones ------------------------------ */

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'accent' | 'ghost' | 'quiet' | 'danger'; icon?: string; size?: 'sm' | 'md'; busy?: boolean }
export function Button({ variant = 'ghost', icon, size = 'md', busy, children, className = '', disabled, ...rest }: BtnProps) {
  return (
    <button {...rest} disabled={disabled || busy} className={`btn btn-${variant} btn-${size} ${className}`}>
      {busy ? <span className="spinner" /> : icon ? <Icon name={icon} size={size === 'sm' ? 15 : 17} /> : null}
      {children && <span>{children}</span>}
    </button>
  )
}

export function IconButton({ icon, label, onClick, className = '' }: { icon: string; label: string; onClick?: () => void; className?: string }) {
  return (
    <button type="button" className={`icon-btn ${className}`} onClick={onClick} aria-label={label} title={label}>
      <Icon name={icon} size={17} />
    </button>
  )
}

/** Confirmación dentro de la página (los diálogos del navegador no siempre están disponibles) */
export function ConfirmButton({ label, confirmLabel = '¿Seguro?', onConfirm, icon = 'trash' }: { label: string; confirmLabel?: string; onConfirm: () => void | Promise<void>; icon?: string }) {
  const [armed, setArmed] = useState(false)
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (!armed) return; const t = setTimeout(() => setArmed(false), 4000); return () => clearTimeout(t) }, [armed])
  return armed
    ? <Button variant="danger" size="sm" busy={busy} onClick={async () => { setBusy(true); try { await onConfirm() } finally { setBusy(false); setArmed(false) } }}>{confirmLabel}</Button>
    : <Button variant="quiet" size="sm" icon={icon} onClick={() => setArmed(true)}>{label}</Button>
}

/* ------------------------------ Paneles ------------------------------ */

export function Drawer({ open, onClose, title, eyebrow, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; eyebrow?: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', k)
    document.body.classList.add('no-scroll')
    return () => { document.removeEventListener('keydown', k); document.body.classList.remove('no-scroll') }
  }, [open, onClose])
  if (!open) return null
  return createPortal(
    <div className="overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <aside className={`drawer ${wide ? 'drawer-wide' : ''}`} role="dialog" aria-modal="true">
        <header className="drawer-head">
          <div>
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            <h2 className="drawer-title">{title}</h2>
          </div>
          <IconButton icon="x" label="Cerrar" onClick={onClose} />
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-foot">{footer}</footer>}
      </aside>
    </div>,
    document.body,
  )
}

export function Modal({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode }) {
  if (!open) return null
  return createPortal(
    <div className="overlay overlay-center" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true">
        <header className="drawer-head"><h2 className="drawer-title">{title}</h2><IconButton icon="x" label="Cerrar" onClick={onClose} /></header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="drawer-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  )
}

/* ------------------------------- Estados ------------------------------- */

const TONES: Record<string, string> = {
  // propiedades
  'Captación': 'sand', 'Disponible': 'olive', 'Reservada': 'terra', 'Vendida': 'ink',
  // comercial
  'Nuevo': 'sand', 'Contactado': 'sand', 'Calificado': 'olive-soft', 'Visitando': 'olive', 'Negociando': 'terra', 'Cerrado': 'ink', 'Perdido': 'muted',
  // contenido / academia / tareas
  'Idea': 'sand', 'Grabado': 'terra', 'Publicado': 'olive', 'En preparación': 'terra-soft', 'Editado': 'olive-soft', 'Listo para publicar': 'olive',
  'Por hacer': 'sand', 'En curso': 'terra', 'Hecha': 'olive',
  // agenda
  'Programada': 'sand', 'Confirmada': 'olive', 'Realizada': 'ink', 'Cancelada': 'muted',
  'Pendiente': 'terra-soft', 'Enviado': 'olive', 'Error': 'danger', 'Desactivado': 'muted',
  // documentos
  'Borrador': 'sand', 'En revisión': 'terra-soft', 'Listo para firmar': 'olive-soft', 'Firmado': 'olive',
  'Aprobado': 'olive', 'Alta': 'terra', 'Media': 'sand', 'Baja': 'muted',
}
export const toneOf = (s?: string) => TONES[s ?? ''] ?? 'sand'

export const Badge = ({ children, tone }: { children: ReactNode; tone?: string }) =>
  children ? <span className={`badge tone-${tone ?? toneOf(String(children))}`}>{children}</span> : null

export const Empty = ({ title, text, action }: { title: string; text?: string; action?: ReactNode }) => (
  <div className="empty">
    <Star size={22} />
    <h3>{title}</h3>
    {text && <p>{text}</p>}
    {action}
  </div>
)

export const Skeleton = ({ rows = 3 }: { rows?: number }) => (
  <div className="skeleton-list">{Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton" />)}</div>
)

export const ErrorNote = ({ error, onRetry }: { error: string; onRetry?: () => void }) => (
  <div className="error-note" role="alert">
    <strong>No se pudo cargar.</strong> <span>{error}</span>
    {onRetry && <Button size="sm" variant="quiet" icon="refresh" onClick={onRetry}>Reintentar</Button>}
  </div>
)

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string; icon?: string }[] }) {
  return (
    <div className="segmented" role="tablist">
      {options.map(o => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.icon && <Icon name={o.icon} size={15} />}<span>{o.label}</span>
        </button>
      ))}
    </div>
  )
}

export const PageHeader = ({ eyebrow, title, subtitle, actions }: { eyebrow?: string; title: string; subtitle?: ReactNode; actions?: ReactNode }) => (
  <header className="page-head">
    <div className="page-head-text">
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h1>{title}</h1>
      {subtitle && <p className="page-sub">{subtitle}</p>}
    </div>
    {actions && <div className="page-actions">{actions}</div>}
  </header>
)

export const Avatar = ({ name }: { name?: string }) =>
  name ? <span className={`avatar av-${name === 'Leticia' ? 'l' : 'y'}`} title={name}>{name[0]}</span> : null

/* -------------------------------- Toasts -------------------------------- */

type Toast = { id: number; text: string; tone: 'ok' | 'error' }
const ToastCtx = createContext<(text: string, tone?: 'ok' | 'error') => void>(() => {})
export const useToast = () => useContext(ToastCtx)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const n = useRef(0)
  const push = useCallback((text: string, tone: 'ok' | 'error' = 'ok') => {
    const id = ++n.current
    setToasts(t => [...t, { id, text, tone }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), tone === 'error' ? 6500 : 3200)
  }, [])
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map(t => <div key={t.id} className={`toast toast-${t.tone}`}>{t.tone === 'ok' ? <Star size={13} color="currentColor" /> : <Icon name="x" size={14} />}<span>{t.text}</span></div>)}
      </div>
    </ToastCtx.Provider>
  )
}

/* ---------------------------- Utilidades UI ---------------------------- */

export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true } catch {
    const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select()
    let ok = false; try { ok = document.execCommand('copy') } catch { /* sin acceso al portapapeles */ }
    ta.remove(); return ok
  }
}
